import { useState, useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { TbSearch, TbX, TbPlus, TbArrowLeft } from "react-icons/tb";
import { PiHandArrowDown } from "react-icons/pi";
import { FaUserInjured } from "react-icons/fa6";
import { apiRequest } from "src/services/apiClient";
import { useEscKey } from "src/hooks/useEscKey";
import { sanitizeInput, trimInput, normalizePlz } from "src/helpers/sanitizeInput";
import MultiPhoneInput from "src/components/PhoneInput/MultiPhoneInput";
import { findPersonDuplicates, type DuplicateRecipient } from "src/helpers/patientDuplicates";
import "./changeRecipientPopup.scss";

/** Local calendar day — a birth date stored as Berlin midnight is the day before in UTC. */
function localDay(value: string): string {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "";
    const pad = (n: number) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

interface Recipient {
    id:         string;
    firstName?: string;
    lastName:   string;
    salutation?: string;
    title?:     string;
    email?:     string;
}

interface Props {
    patientId:           string;
    currentRecipientId?: string;
    /** false for a minor without a recipient — she needs a parent/guardian, never herself. */
    allowSelf?:          boolean;
    onClose:             () => void;
    onChanged:           (newRecipientId: string) => void;
}

type Mode = "pick" | "create";

export function ChangeRecipientPopup({ patientId, currentRecipientId, allowSelf = true, onClose, onChanged }: Props) {
    const { t } = useTranslation();
    useEscKey(onClose, true);

    // ── pick mode ──────────────────────────────────────────────────────────────
    const [mode,       setMode]       = useState<Mode>("pick");
    const [recipients, setRecipients] = useState<Recipient[]>([]);
    const [loading,    setLoading]    = useState(true);
    const [saving,     setSaving]     = useState(false);
    const [search,     setSearch]     = useState("");
    const [selected,   setSelected]   = useState<string | "self" | null>(null);
    const [error,      setError]      = useState<string | null>(null);

    useEffect(() => {
        apiRequest<Recipient[]>("/recipients")
            .then(d => setRecipients(Array.isArray(d) ? d : []))
            .catch(() => setError(t("patientPage.failedLoadRecipients")))
            .finally(() => setLoading(false));
    }, [t]);

    // The patient already exists as a recipient paying for herself (e.g. a mother paying for her
    // children) — "pays for themselves" would create her a second time, so offer that one instead.
    const [selfRecipients, setSelfRecipients] = useState<DuplicateRecipient[]>([]);
    useEffect(() => {
        let cancelled = false;
        apiRequest<{ firstName?: string; lastName?: string; birthDate?: string }>(`/patients/${patientId}`)
            .then((p) => {
                const day = p.birthDate ? localDay(p.birthDate) : "";
                return p.lastName && day ? findPersonDuplicates(p.firstName ?? "", p.lastName, day) : null;
            })
            // One already marked as another patient would then hold her twice — not offered.
            .then((d) => {
                if (d && !cancelled) setSelfRecipients(d.recipients.filter((r) => r.id !== currentRecipientId && !r.sameAsPatientId));
            })
            .catch(() => { /* only a hint */ });
        return () => { cancelled = true; };
    }, [patientId, currentRecipientId]);

    const filtered = useMemo(() => {
        const q = search.toLowerCase();
        return recipients.filter(r =>
            r.id !== currentRecipientId &&
            (`${r.firstName ?? ""} ${r.lastName} ${r.email ?? ""}`.toLowerCase().includes(q)),
        );
    }, [recipients, search, currentRecipientId]);

    const formatName = (r: Recipient) =>
        [r.title, r.firstName, r.lastName].filter(Boolean).join(" ");

    const handleConfirm = async () => {
        if (!selected) return;
        setSaving(true);
        setError(null);
        try {
            const res = await apiRequest<{ recipientId: string }>(`/patients/${patientId}/change-recipient`, {
                method: "POST",
                body: JSON.stringify(
                    selected === "self"
                        ? { selfRecipient: true }
                        : { recipientId: selected },
                ),
            }, { "Content-Type": "application/json" });
            // Moved to the recipient that is this same person — mark her as that recipient herself.
            const self = selfRecipients.find((r) => r.id === selected);
            if (self && !self.sameAsPatientId) {
                await apiRequest(`/recipients/${self.id}/same-as-patient`, {
                    method: "POST",
                    body: JSON.stringify({ patientId }),
                }, { "Content-Type": "application/json" });
            }
            onClose();
            onChanged(res.recipientId);
        } catch {
            setError(t("patientPage.changeRecipientFailed"));
            setSaving(false);
        }
    };

    // ── create mode ────────────────────────────────────────────────────────────
    const SALUTATIONS = [
        { value: "H", label: t("form.herr") },
        { value: "F", label: t("form.frau") },
    ];
    const [newSal,    setNewSal]    = useState("H");
    const [newTitle,  setNewTitle]  = useState("");
    const [newFirst,  setNewFirst]  = useState("");
    const [newLast,   setNewLast]   = useState("");
    const [newEmail,  setNewEmail]  = useState("");
    const [newTel,    setNewTel]    = useState<string[]>([]);
    const [newStreet, setNewStreet] = useState("");
    const [newPlz,    setNewPlz]    = useState("");
    const [newCity,   setNewCity]   = useState("");
    const [creating,  setCreating]  = useState(false);

    const handleCreate = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!newLast.trim()) { setError(t("createRecipient.validationError")); return; }
        setCreating(true);
        setError(null);
        try {
            const praxisId   = JSON.parse(localStorage.getItem("praxis") || "null")?.id ?? "";
            const doctorId   = JSON.parse(localStorage.getItem("doctor") || "null")?.id ?? "";
            const recipientId = crypto.randomUUID();
            await apiRequest("/recipients", {
                method: "POST",
                body: JSON.stringify({
                    id: recipientId, praxisId, doctorId,
                    salutation: newSal,
                    title:      newTitle   || undefined,
                    firstName:  newFirst   || undefined,
                    lastName:   newLast,
                    email:      newEmail   || undefined,
                    tel:        newTel.filter(Boolean),
                    street:     newStreet  || "",
                    plz:        newPlz     || "",
                    city:       newCity    || "",
                }),
            }, { "Content-Type": "application/json" });
            await apiRequest(`/patients/${patientId}/change-recipient`, {
                method: "POST",
                body: JSON.stringify({ recipientId }),
            }, { "Content-Type": "application/json" });
            onClose();
            onChanged(recipientId);
        } catch {
            setError(t("patientPage.changeRecipientFailed"));
        } finally {
            setCreating(false);
        }
    };

    return createPortal(
        <div className="popup-overlay" onClick={onClose}>
            <div className="popup popup--wide change-recipient-popup" onClick={e => e.stopPropagation()}>
                <div className="popup__header">
                    <span className="popup__title">{t("patientPage.changeRecipientTitle")}</span>
                    <button className="popup__close" onClick={onClose}><TbX /></button>
                </div>

                {mode === "pick" ? (
                    <>
                        <div className="popup__message popup__message--scroll">
                            {/* Own recipient option */}
                            {allowSelf ? (
                                <button
                                    type="button"
                                    className={`crp-option crp-option--self${selected === "self" ? " crp-option--active" : ""}`}
                                    onClick={() => setSelected(selected === "self" ? null : "self")}
                                >
                                    <div className="crp-option__icon"><FaUserInjured /></div>
                                    <div className="crp-option__text">
                                        <strong>{t("patientPage.changeRecipientOwnOption")}</strong>
                                        <span>{t("patientPage.changeRecipientOwnDesc")}</span>
                                    </div>
                                </button>
                            ) : (
                                <p className="crp-minor-note">{t("patientPage.changeRecipientMinorNote")}</p>
                            )}

                            {allowSelf && selected === "self" && selfRecipients.length > 0 && (
                                <div className="crp-dup">
                                    <p className="crp-dup__text">{t("patientPage.changeRecipientSelfExists")}</p>
                                    {selfRecipients.map((r) => (
                                        <button key={r.id} type="button" className="crp-option" onClick={() => setSelected(r.id)}>
                                            <div className="crp-option__icon"><PiHandArrowDown /></div>
                                            <div className="crp-option__text">
                                                <strong>{formatName(r)}</strong>
                                                <span>{t("patientPage.changeRecipientSelfExistsPick")}</span>
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            )}

                            <div className="crp-divider">
                                <span className="crp-divider__line" />
                                <span className="crp-divider__label">oder</span>
                                <span className="crp-divider__line" />
                            </div>

                            {/* Search */}
                            <div className="crp-search">
                                <TbSearch className="crp-search__icon" />
                                <input
                                    className="crp-search__input"
                                    placeholder={t("patientPage.changeRecipientSearch")}
                                    value={search}
                                    onChange={e => setSearch(e.target.value)}
                                    autoFocus
                                />
                            </div>

                            {/* Recipient list */}
                            <div className="crp-list">
                                {loading && <div className="crp-state">…</div>}
                                {!loading && filtered.length === 0 && (
                                    <div className="crp-state">{search ? "–" : t("patientPage.noRecipient")}</div>
                                )}
                                {filtered.map(r => (
                                    <button
                                        key={r.id}
                                        type="button"
                                        className={`crp-option${selected === r.id ? " crp-option--active" : ""}`}
                                        onClick={() => setSelected(selected === r.id ? null : r.id)}
                                    >
                                        <div className="crp-option__icon"><PiHandArrowDown /></div>
                                        <div className="crp-option__text">
                                            <strong>{formatName(r)}</strong>
                                            {r.email && <span>{r.email}</span>}
                                        </div>
                                    </button>
                                ))}
                            </div>

                            {error && <div className="form-error" style={{ marginTop: 8 }}>{error}</div>}
                        </div>

                        <div className="popup__actions" style={{ padding: "12px 0 0" }}>
                            <button
                                type="button"
                                className="popup__btn popup__btn--default crp-new-btn"
                                onClick={() => { setMode("create"); setError(null); }}
                            >
                                <TbPlus /> {t("patientPage.changeRecipientNew")}
                            </button>
                            <div style={{ flex: 1 }} />
                            <button className="popup__btn popup__btn--default" onClick={onClose}>
                                {t("popup.cancel")}
                            </button>
                            <button
                                className="popup__btn popup__btn--primary"
                                disabled={!selected || saving}
                                onClick={handleConfirm}
                            >
                                {saving ? "…" : t("patientPage.changeRecipientConfirm")}
                            </button>
                        </div>
                    </>
                ) : (
                    <form onSubmit={handleCreate}>
                        <div className="popup__message crp-create-form">
                            <div className="crp-create-row">
                                <div className="crp-create-field crp-create-field--short">
                                    <label>{t("form.salutation")}</label>
                                    <select value={newSal} onChange={e => setNewSal(e.target.value)}>
                                        {SALUTATIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                                    </select>
                                </div>
                                <div className="crp-create-field crp-create-field--short">
                                    <label>{t("form.title")}</label>
                                    <input value={newTitle} maxLength={20} placeholder={t("form.drPlaceholder")} autoFocus
                                        onChange={e => setNewTitle(sanitizeInput(e.target.value))}
                                        onBlur={e => setNewTitle(trimInput(e.target.value))} />
                                </div>
                            </div>
                            <div className="crp-create-row">
                                <div className="crp-create-field">
                                    <label>{t("form.firstName")}</label>
                                    <input value={newFirst} maxLength={50}
                                        onChange={e => setNewFirst(sanitizeInput(e.target.value))}
                                        onBlur={e => setNewFirst(trimInput(e.target.value))} />
                                </div>
                                <div className="crp-create-field">
                                    <label>{t("form.lastName")} *</label>
                                    <input value={newLast} maxLength={50}
                                        onChange={e => setNewLast(sanitizeInput(e.target.value))}
                                        onBlur={e => setNewLast(trimInput(e.target.value))} />
                                </div>
                            </div>
                            <div className="crp-create-row">
                                <div className="crp-create-field">
                                    <label>{t("form.email")}</label>
                                    <input type="email" value={newEmail}
                                        onChange={e => setNewEmail(sanitizeInput(e.target.value))}
                                        onBlur={e => setNewEmail(trimInput(e.target.value))} />
                                </div>
                            </div>
                            <div className="crp-create-row">
                                <div className="crp-create-field">
                                    <label>{t("form.phone")}</label>
                                    <MultiPhoneInput value={newTel} onChange={setNewTel} />
                                </div>
                            </div>
                            <div className="crp-create-row">
                                <div className="crp-create-field">
                                    <label>{t("form.street")}</label>
                                    <input value={newStreet}
                                        onChange={e => setNewStreet(sanitizeInput(e.target.value))}
                                        onBlur={e => setNewStreet(trimInput(e.target.value))} />
                                </div>
                            </div>
                            <div className="crp-create-row">
                                <div className="crp-create-field crp-create-field--short">
                                    <label>{t("form.plz")}</label>
                                    <input value={newPlz} onChange={e => setNewPlz(normalizePlz(e.target.value))} />
                                </div>
                                <div className="crp-create-field">
                                    <label>{t("form.city")}</label>
                                    <input value={newCity}
                                        onChange={e => setNewCity(sanitizeInput(e.target.value))}
                                        onBlur={e => setNewCity(trimInput(e.target.value))} />
                                </div>
                            </div>
                            {error && <div className="form-error">{error}</div>}
                        </div>

                        <div className="popup__actions" style={{ padding: "12px 0 0" }}>
                            <button type="button" className="popup__btn popup__btn--default"
                                onClick={() => { setMode("pick"); setError(null); }}>
                                <TbArrowLeft /> {t("createRecipient.back")}
                            </button>
                            <div style={{ flex: 1 }} />
                            <button type="button" className="popup__btn popup__btn--default" onClick={onClose}>
                                {t("popup.cancel")}
                            </button>
                            <button type="submit" className="popup__btn popup__btn--primary" disabled={creating}>
                                {creating ? "…" : t("patientPage.changeRecipientCreate")}
                            </button>
                        </div>
                    </form>
                )}
            </div>
        </div>,
        document.body,
    );
}
